import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import { ApprovalCountsEntity } from "../../domain/entities/ApprovalEntity";

interface ApprovalSidebarProps {
  counts: ApprovalCountsEntity | null;
  selectedEntityType: string | null;
  onEntityTypeChange: (entityType: string) => void;
  status: "pending" | "approved" | "rejected" | null;
}

const ENTITY_TYPE_DISPLAY: Record<string, string> = {
  invoice: "Invoices",
  bill: "Bills",
  customer: "Customers",
  vendor: "Vendors",
  credit_note: "Credit Note",
  debit_note: "Debit Note",
  bank_transaction: "Cash and Bank",
  bank_account: "Bank Accounts",
  journal: "Journal",
  ap_ar_journal: "AP/AR Journal",
  gl: "General Ledger",
  month_end_checklist_item: "Month End Checklist",
  tenant_compliance_calendar: "Compliance",
};

const ENTITY_TYPE_ORDER = [
  "invoice",
  "bill",
  "customer",
  "vendor",
  "credit_note",
  "debit_note",
  "bank_transaction",
  "bank_account",
  "journal",
  "ap_ar_journal",
  "gl",
  "month_end_checklist_item",
  "tenant_compliance_calendar",
];

export const ApprovalSidebar: React.FC<ApprovalSidebarProps> = ({
  counts,
  selectedEntityType,
  onEntityTypeChange,
  status,
}) => {
  const getCountForEntityType = (entityType: string): number => {
    if (!counts || !counts[entityType as keyof ApprovalCountsEntity]) {
      return 0;
    }
    const entityCounts = counts[entityType as keyof ApprovalCountsEntity] as {
      pending: number;
      approved: number;
      rejected: number;
    };

    // Return count based on selected status
    if (status === "pending") {
      return entityCounts.pending;
    } else if (status === "approved") {
      return entityCounts.approved;
    } else if (status === "rejected") {
      return entityCounts.rejected;
    }

    // If no status selected, return total
    return entityCounts.pending + entityCounts.approved + entityCounts.rejected;
  };

  return (
    <div className="flex w-64 flex-none flex-col items-start gap-6 self-stretch border-r border-solid border-neutral-border bg-default-background px-4 py-6 overflow-auto">
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        ACCOUNTING AREAS
      </span>
      <div className="flex w-full flex-col items-start gap-2">
        {ENTITY_TYPE_ORDER.map((entityType) => {
          const count = getCountForEntityType(entityType);
          const isSelected = selectedEntityType === entityType;
          const displayName = ENTITY_TYPE_DISPLAY[entityType];

          return (
            <div
              key={entityType}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-3 cursor-pointer ${
                isSelected ? "bg-brand-50" : ""
              }`}
              onClick={() => onEntityTypeChange(entityType)}
            >
              <span
                className={`grow shrink-0 basis-0 whitespace-pre-wrap ${
                  isSelected
                    ? "text-body-bold font-body-bold text-default-font"
                    : "text-body font-body text-default-font"
                }`}
              >
                {displayName}
              </span>
              {count > 0 ? (
                <Badge
                  variant={
                    status === "pending"
                      ? "error"
                      : status === "approved"
                        ? "success"
                        : status === "rejected"
                          ? "warning"
                          : "neutral"
                  }
                >
                  {count}
                </Badge>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
